import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { AbWebServices } from "abweb/service/AbWebServices";
import { ImageType } from "abweb/service/image/api/ImageType";
import { Assert } from "abweb/utils/Assert";
import * as React from "react";

/**
 * Properties of the AbImage component.
 */

/**
 * The **AbImage** component displays an image, which can optionally be dragged or clicked to perform an action.
 */
const AbImage = (props, ref) => {
  const onClicked = e => {
    if (props.onClick !== undefined) {
      e.preventDefault();
      props.onClick(e);
    }
  };
  const sharedProps = {
    "data-ab-image-key": props.image,
    "data-test-component": "ab-image",
    onClick: onClicked
  };
  const imgSharedProps = {
    width: props.width,
    height: props.height,
    alt: props.altText,
    draggable: props.draggable
  };

  // AbSDK icons from the Confluence SDK image registry use this SVG render function.
  const renderSVG = identifierInfo => {
    const colorAttr = getColorAttr(identifierInfo);
    // eslint-disable-next-line react/forbid-elements
    return /*#__PURE__*/React.createElement("img", _extends({}, ReactUtils.componentProps("ab-image", props, [!isToggleable(identifierInfo) && "ab-not-toggleable"]), sharedProps, imgSharedProps, colorAttr, {
      src: identifierInfo.scales[0].path,
      ref: ReactUtils.castRef(ref)
    }));
  };
  const renderImage = identifierInfo => {
    const resourcePath = getImageResourcePath(identifierInfo, props.width, props.height);
    // eslint-disable-next-line react/forbid-elements
    return /*#__PURE__*/React.createElement("img", _extends({}, ReactUtils.componentProps("ab-image", props, [!isToggleable(identifierInfo) && "ab-not-toggleable"]), sharedProps, imgSharedProps, {
      src: resourcePath,
      ref: ReactUtils.castRef(ref)
    }));
  };
  const renderFontBased = identifierInfo => {
    let fontSize;
    const height = props.height;
    const width = props.width;
    if (typeof height === "string") {
      fontSize = height;
    } else {
      const size = height === undefined || isNaN(height) ? 16 : height;
      if (size !== 16) {
        // We consider 16 the default size for a font-based icon which means don't alter the font-size
        fontSize = size.toString() + "px";
      }
    }
    const extraClasses = identifierInfo.elementClasses ? [identifierInfo.elementClasses] : [];
    const element = /*#__PURE__*/React.createElement("i", _extends({}, ReactUtils.componentProps("ab-image", props, [...extraClasses, !isToggleable(identifierInfo) && "ab-not-toggleable"]), sharedProps, {
      style: {
        ...props.style,
        height,
        width,
        fontSize
      },
      "aria-hidden": "true"
    }));
    if (identifierInfo.spanClasses) {
      return /*#__PURE__*/React.createElement("span", {
        className: identifierInfo.spanClasses,
        style: {
          height,
          width
        }
      }, element);
    } else {
      return element;
    }
  };
  const renderElement = () => {
    const identifierInfo = AbWebServices.getImageIdentifierManager().getIdentifierInfo(props.image);
    Assert.assert(identifierInfo.type in ImageType, "Unrecognized ImageType: " + identifierInfo.type);
    switch (identifierInfo.type) {
      case ImageType.SVG:
        return renderSVG(identifierInfo);
      case ImageType.IMAGE:
        return renderImage(identifierInfo);
      case ImageType.FONT_BASED:
        return renderFontBased(identifierInfo);
    }
  };
  return /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: props.tooltip
  }, renderElement());
};
const WrappedAbImage = /*#__PURE__*/React.forwardRef(AbImage);
export { WrappedAbImage as AbImage };
const isToggleable = identifierInfo => {
  /* Changed to a "noToggle" flag in SDK database.  NoToggle means don't allow toggling.   */
  return identifierInfo.extra != null && identifierInfo.extra.flags != null && !(identifierInfo.extra.flags.indexOf("notoggle") >= 0);
};

/**
 * Color attribute based on Confluence image registry.
 */
const getColorAttr = identifierInfo => {
  const value = identifierInfo.extra?.color;
  return value === undefined ? {} : {
    "data-ab-image-color": value
  };
};

/**
 * Retrieves the URI of the image specified. Note that this only works with bitmap/vector images, not font-based
 * iconography.
 * @param identifierInfo Specification of which image to retrieve.
 * @param width (optional) Desired image width.
 * @param height (optional) Desired image height.
 * @returns {string} URI of image resource.
 */
const getImageResourcePath = (identifierInfo, width = NaN, height = NaN) => {
  let resourcePath = null;
  if (typeof width === "number" && !isNaN(width) || typeof height === "number" && !isNaN(height)) {
    identifierInfo.scales.some(scale => {
      if (width === scale.width && height === scale.height) {
        resourcePath = scale.path;
        return true;
      } else {
        return false;
      }
    });

    // Look for match on either width or height
    if (resourcePath === null) {
      identifierInfo.scales.some(scale => {
        if (width === scale.width || height === scale.height) {
          resourcePath = scale.path;
          return true;
        } else {
          return false;
        }
      });
    }
  }
  // None found, then just use the first one in the list.
  if (resourcePath === null) {
    resourcePath = identifierInfo.scales[0].path;
  }
  return resourcePath;
};
//# sourceMappingURL=AbImage.js.map
